'use client';

import CustomButton from '@/components/custom-button';
import CustomRePassword from '@/components/custom-re-password';
import { template_error } from '@/components/custom-toast/sonner-with-template';
import CustomToast from '@/components/custom-toast';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { regexUserName } from '@/constant/regexs';
import { pageDirectories } from '@/constant/route';
import { MessageResource } from '@/i18n/messages';
import { Link } from '@/navigation';
import { forgotPassword } from '@/server/api/v1/auth/auth.action';
import { ResetCodeType } from '@/types/enum';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronLeft, CircleAlert, KeyRound } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const { sonner } = CustomToast;

const ForgotPasswordSchema = (
    t: (msg: keyof MessageResource['Forgor']) => string
) =>
    z.object({
        userName: z
            .string()
            .min(4, { message: t('Forgor_UsernameMustBeAtLeast4CharactersLong') })
            .regex(regexUserName, { message: t('Forgor_InvalidUserNamePlsCheckAgain') }),
    });

type ForgotPasswordForm = z.infer<ReturnType<typeof ForgotPasswordSchema>>;

export default function SsoForgorForm() {
    const transDef = useTranslations('System');
    const transPage = useTranslations('Forgor');

    const [isLoading, setIsLoading] = useState(false);
    const [isOTPStep, setIsOTPStep] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [forgotInfo, setForgotInfo] = useState<{ userName: string }>({
        userName: '',
    });
    const [otpError, setOtpError] = useState<{
        isError: boolean;
        message?: string;
    }>({ isError: false });

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<ForgotPasswordForm>({
        mode: 'onBlur',
        resolver: zodResolver(ForgotPasswordSchema(transPage)),
        defaultValues: { userName: '' },
    });

    const onSubmit = async (data: ForgotPasswordForm) => {
        setIsLoading(true);
        const result = await forgotPassword({ ...data, type: ResetCodeType.OTP });
        if (result?.data?.isSuccessStatus) {
            setForgotInfo({ userName: data.userName });
            setIsOTPStep(true);
        } else {
            sonner.error(
                template_error({
                    title: transDef('System_AnErrorHasOccurred'),
                    desc: result?.data?.errors,
                })
            );
            setIsLoading(false);
        }
    };

    if (isSuccess) {
        return (
            <div className='w-full py-10 text-center'>
                <div className='relative mx-auto mb-5 flex h-20 w-20 items-center justify-center'>
                    <div className='absolute inset-0 animate-ping rounded-full bg-green-200 opacity-75 duration-1000' />
                    <div className='relative rounded-full bg-green-500 p-4'>
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            fill='none'
                            viewBox='0 0 24 24'
                            strokeWidth='2'
                            stroke='currentColor'
                            className='h-8 w-8 text-white'
                        >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                d='M4.5 12.75l6 6 9-13.5'
                            />
                        </svg>
                    </div>
                </div>
                <h3 className='my-3 text-2xl font-semibold text-gray-700'>
                    {transPage('Forgor_SucessfullyChangePassword')}!
                </h3>
                <Link
                    href={pageDirectories['Signin']}
                    className='text-sm text-primary hover:underline'
                >
                    {transPage('Forgor_BackToLogin')}
                </Link>
            </div>
        );
    }

    if (isOTPStep) {
        return (
            <div className='space-y-4'>
                <button
                    type='button'
                    onClick={() => {
                        setIsOTPStep(false);
                        setIsLoading(false);
                        setOtpError({ isError: false });
                        setForgotInfo({ userName: '' });
                    }}
                    className='flex items-center gap-1 text-sm text-primary hover:underline'
                >
                    <ChevronLeft size={14} />
                    {transPage('Forgor_GoBack')}
                </button>

                <h1 className='text-[26px] font-bold tracking-wide text-primary lg:text-3xl'>
                    {transPage('Forgor_ResetPassword')}
                </h1>

                {otpError.isError && (
                    <Alert className='bg-red-50 py-2 text-red-600'>
                        <CircleAlert className='h-4 w-4' />
                        <AlertTitle className='text-sm'>
                            {transDef('System_AnErrorHasOccurred')}
                        </AlertTitle>
                        <AlertDescription className='text-xs'>
                            {otpError.message}
                        </AlertDescription>
                    </Alert>
                )}

                <CustomRePassword.WithOTP
                    username={forgotInfo.userName}
                    onSuccess={() => setIsSuccess(true)}
                    onError={(errs) =>
                        setOtpError({
                            isError: true,
                            message: errs.join(', ') ?? transPage('Forgor_PlsTryAgain'),
                        })
                    }
                    onClearAlert={() => setOtpError({ isError: false })}
                />
            </div>
        );
    }

    return (
        <div className='space-y-4'>
            <div className='flex items-center gap-2'>
                <KeyRound size={22} className='text-primary' />
                <h1 className='text-[26px] font-bold tracking-wide text-primary lg:text-3xl'>
                    {transPage('Forgor_PageTitle')}
                </h1>
            </div>

            <p className='text-sm text-muted-foreground'>
                {transPage('Forgor_EnterRegistrationAccountNameInformation')}
            </p>

            <form className='space-y-4' onSubmit={handleSubmit(onSubmit)}>
                <div>
                    <Label htmlFor='forgor-username' className='mb-1.5 text-sm font-medium'>
                        {transPage('Forgor_EnterYourAccountName')}
                    </Label>
                    <Input
                        id='forgor-username'
                        type='text'
                        autoFocus
                        disabled={isLoading}
                        placeholder={transPage('Forgor_EnterYourAccountName') + '...'}
                        className='rounded-sm shadow-sm'
                        {...register('userName')}
                    />
                    {errors.userName && (
                        <p className='mt-1 text-xs text-red-500'>
                            {errors.userName.message}
                        </p>
                    )}
                </div>

                <CustomButton.Root
                    type='submit'
                    isLoading={isLoading}
                    className='w-full font-semibold!'
                >
                    {transPage('Forgor_Next')}
                </CustomButton.Root>
            </form>

            <p className='text-center text-xs text-muted-foreground'>
                {transPage('Forgor_HaveAccount')}{' '}
                <Link
                    href={pageDirectories['Signin']}
                    className='font-semibold text-primary hover:underline'
                >
                    {transPage('Forgor_BackToLogin')}
                </Link>
            </p>
        </div>
    );
}
